<template>
  <!-- Action Components -->
  <CopyToListAction
    ref="copyToListRef"
    @confirm="onCopyConfirm"
  />
  <CreateListAction
    ref="createListRef"
    @confirm="onCreateConfirm"
  />
  <!-- Facturation V5 : (dé)marquer des contacts comme facturables -->
  <SetBillableAction
    ref="setNonBillableRef"
    mode="non_billable"
    @confirm="onBillableConfirm"
  />
  <SetBillableAction
    ref="setBillableRef"
    mode="billable"
    @confirm="onBillableConfirm"
  />
  <!-- RGPD : effacement définitif des données personnelles (SC-3851) -->
  <GdprEraseAction
    ref="gdprEraseRef"
    @confirm="onGdprEraseConfirm"
  />

  <div class="list-contacts-page">
    <PageHeader
      :title="listName"
      :summary="t('myLists.contacts.summary')"
    />

    <!-- Content Section with DataTable -->
    <div class="page-content">
      <div class="ListsManage">
        <SpmTableAPI
          v-if="apiCall && columns.length > 0"
          ref="childComponentRef"
          :api-call="apiCall"
          :columns="columns"
          :show-reload-button="true"
          :expandable="true"
          data-key="id_contact"
          :enable-grouped-actions="true"
          :grouped-actions-options="groupedActions"
          :row-actions-options="rowActions"
          :exportable="true"
          :show-export-all-button="true"
          :export-config="{ entityType: 'contacts' }"
          @export="handleExport"
        >
          <!-- Composite column: fullname (firstname, lastname, created_at, updated_at) -->
          <template #cell-fullname="{ data }">
            <div class="fullname-cell">
              <div class="name-row">
                <span
                  v-if="formatFullName(data.first_name, data.last_name)"
                  class="contact-name"
                >
                  {{ formatFullName(data.first_name, data.last_name) }}
                </span>
                <span
                  v-else
                  class="contact-name contact-name--empty"
                >
                  {{ t('myLists.manage.customers.noName') }}
                </span>
              </div>
              <div class="dates-row">
                <SpmDateDisplay
                  :value="data.created_at"
                  format="date"
                  icon="fa-light fa-calendar-circle-plus"
                  size="sm"
                />
                <SpmDateDisplay
                  :value="data.updated_at"
                  format="date"
                  icon="fa-light fa-edit"
                  size="sm"
                />
              </div>
            </div>
          </template>

          <!-- Single column: email -->
          <template #cell-email="{ data }">
            <span v-if="cleanStr(data.email)">
              <DataExplorerLink
                :id="data.id_contact"
                :value="cleanStr(data.email)"
                :show-icon="true"
              />
            </span>
            <span
              v-else
              class="cell-empty"
            >-</span>
          </template>

          <!-- Single column: sms -->
          <template #cell-sms="{ data }">
            <div class="text-center w-full flex justify-content-center">
              <span v-if="cleanStr(data.phone)">{{ formatPhoneNumber(data.phone) }}</span>
              <i
                v-else
                v-tooltip.top="t('myLists.manage.customers.noPhone')"
                class="fa-light fa-phone-slash cell-icon cell-icon--inactive"
              />
            </div>
          </template>

          <!-- Single column: push -->
          <template #cell-push="{ data }">
            <div class="text-center w-full flex justify-content-center">
              <i
                v-if="cleanStr(data.push)"
                v-tooltip.top="t('myLists.manage.customers.hasPush')"
                class="fa-light fa-bell cell-icon cell-icon--active"
              />
              <i
                v-else
                v-tooltip.top="t('myLists.manage.customers.noPush')"
                class="fa-light fa-bell-slash cell-icon cell-icon--inactive"
              />
            </div>
          </template>

          <!-- Facturation V5 : badge facturable / non-facturable -->
          <template #cell-billable="{ data }">
            <div class="text-center w-full flex justify-content-center">
              <span
                v-if="Number(data.is_billable) === 1"
                v-tooltip.top="t('myLists.manage.customers.billable.tooltipYes')"
                class="billable-badge billable-badge--yes"
              >{{ t('myLists.manage.customers.billable.yes') }}</span>
              <span
                v-else
                v-tooltip.top="Number(data.do_not_charge) === 1
                  ? t('myLists.manage.customers.billable.reasonExcluded')
                  : t('myLists.manage.customers.billable.reasonUnreachable')"
                class="billable-badge billable-badge--no"
              >{{ t('myLists.manage.customers.billable.no') }}</span>
            </div>
          </template>

          <!-- Composite column: location (country, lang) -->
          <template #cell-location="{ data }">
            <div class="location-cell">
              <!-- Country flag or neutral icon -->
              <div
                v-if="cleanStr(data.country)"
                v-tooltip.top="t(`countries.codes.${cleanStr(data.country).toUpperCase()}`)"
              >
                <Flag :country="cleanStr(data.country)" />
              </div>
              <div
                v-else
                v-tooltip.top="t('myLists.manage.customers.noCountry')"
              >
                <i
                  class="fa fa-globe"
                  style="color: #d1d5db;"
                />
              </div>

              <!-- Language badge -->
              <span
                v-if="cleanStr(data.lang)"
                v-tooltip.top="t(`languages.codes.${cleanStr(data.lang)}`)"
                class="lang-badge cursor-pointer"
              >{{ cleanStr(data.lang).toUpperCase() }}</span>
            </div>
          </template>

          <!-- Lists column with icon and dynamic tooltip -->
          <template #cell-lists="{ data }">
            <div class="text-center w-full flex justify-content-center">
              <i
                :key="getListsTooltipKey(data.id_contact)"
                v-tooltip.top="getListsTooltipContent(data.id_contact)"
                class="fa fa-list-ul cell-icon cursor-pointer"
                :class="getListsIconClass(data.id_contact)"
                @mouseenter="loadContactLists(data.id_contact)"
              />
            </div>
          </template>

          <template #expansion="{ data }">
            <div class="expansion-content">
              <div
                class="channel-status"
                :class="channelState('email', data).cssClass"
              >
                <i class="fa-light fa-envelope" />
                <span class="channel-label">{{ t('myLists.manage.customers.expansion.channels.email') }}</span>
                <span class="channel-value">{{ channelState('email', data).label }}</span>
              </div>
              <div
                class="channel-status"
                :class="channelState('sms', data).cssClass"
              >
                <i class="fa-light fa-mobile-alt" />
                <span class="channel-label">{{ t('myLists.manage.customers.expansion.channels.sms') }}</span>
                <span class="channel-value">{{ channelState('sms', data).label }}</span>
              </div>
              <div
                class="channel-status"
                :class="channelState('push', data).cssClass"
              >
                <i class="fa-light fa-bell" />
                <span class="channel-label">{{ t('myLists.manage.customers.expansion.channels.push') }}</span>
                <span class="channel-value">{{ channelState('push', data).label }}</span>
              </div>
            </div>
          </template>
        </SpmTableAPI>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { useI18n } from 'vue-i18n';
import PageHeader from '@/components/layout/PageHeader.vue';
import SpmTableAPI from '@/components/table-rest-api/SpmTableAPI.vue';
import SpmDateDisplay from '@/components/table-rest-api/SpmDateDisplay.vue';
import type {
  SpmColumnConfig,
  ApiCallConfig,
  SelectionInfo,
  SpmApiFilter,
  ExportPayload,
} from '@/components/table-rest-api/types';
import { NestApiType, nestCall } from '@/composables/nestApi';
import getChannelSubscriptionStatus, {
  ChannelSubscriptionStatus,
  SubscriptionChannel,
} from '@/composables/contacts/channelSubscription';
import { showToastError, showToastSuccess } from '@/helpers';
import {
  defineComponent, onBeforeMount, ref, reactive,
} from 'vue';
import Tooltip from 'primevue/tooltip';
import Flag from '@/components/table/renderers/Flag.vue';
import DataExplorerLink from '@/components/data-explorer/fields/DataExplorerLink.vue';
import { UserState } from '@/composables/User';
import {
  CopyToListAction,
  CreateListAction,
  SetBillableAction,
  GdprEraseAction,
  useContactsActions,
  useOptinFilterFields,
} from './ListsContactsActions';
import type {
  CopyToListResult,
  CreateListResult,
  SetBillableResult,
  GdprEraseResult,
} from './ListsContactsActions';

export default defineComponent({
  name: 'Contacts',

  components: {
    SpmTableAPI,
    PageHeader,
    SpmDateDisplay,
    Flag,
    CopyToListAction,
    CreateListAction,
    SetBillableAction,
    GdprEraseAction,
    DataExplorerLink,
  },

  directives: {
    Tooltip,
  },

  setup() {
    const { t } = useI18n();
    const childComponentRef = ref();
    const listName = ref('');
    const apiCall = ref<ApiCallConfig | null>(null);

    // Lists tooltip state management
    const listsTooltipState = reactive<Record<string, {
      loading: boolean;
      error: boolean;
      lists: Array<{ idList: number; name: string }>;
      loaded: boolean;
    }>>({});

    const setLoading = async (loading: boolean) => {
      if (childComponentRef.value) {
        await childComponentRef.value.setLoading(loading);
      }
    };

    // Action component refs
    const copyToListRef = ref(null);
    const createListRef = ref(null);
    const setBillableRef = ref(null);
    const setNonBillableRef = ref(null);
    const gdprEraseRef = ref(null);

    // Facturation V5 : badge + filtre + actions facturable n'apparaissent que
    // pour les shops dont l'offre est V5 (bundle_version === 15).
    const isV5 = UserState.activeShop?.bundleVersion === 15;

    // Use the composable for grouped and row actions
    const { groupedActions, rowActions } = useContactsActions({
      copyToListRef,
      createListRef,
      setBillableRef,
      setNonBillableRef,
      gdprEraseRef,
    });

    apiCall.value = {
      type_api: NestApiType.CLIENT_API,
      path: '/contacts/all',
      secure: true,
    };

    const columns: SpmColumnConfig[] = [
      // Hidden: ID for selection/actions
      {
        id: 'id_contact',
        header: '',
        fields: ['id_contact'],
        hidden: true,
      },
      // Composite: fullname with firstname, lastname, created_at, updated_at
      {
        id: 'fullname',
        header: t('myLists.manage.customers.headers.fullName'),
        fields: [{
          name: 'first_name',
          label: t('myLists.manage.customers.headers.firstname'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'textInput',
          },
        }, {
          name: 'last_name',
          label: t('myLists.manage.customers.headers.lastname'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'textInput',
          },
        }, {
          name: 'created_at',
          label: t('myLists.manage.customers.headers.creationDate'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'dateRange',
          },
        }, {
          name: 'updated_at',
          label: t('myLists.manage.customers.headers.modifDate'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'dateRange',
          },
        }],
      },
      // Single field columns
      {
        id: 'email',
        header: t('myLists.manage.customers.headers.email'),
        fields: [{
          name: 'email',
          label: t('myLists.manage.customers.headers.email'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'textInput',
          },
        }],
      },
      {
        id: 'sms',
        header: t('myLists.manage.customers.headers.sms'),
        fields: [{
          name: 'phone',
          label: t('myLists.manage.customers.headers.sms'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'textInput',
          },
        }],
      },
      {
        id: 'push',
        header: t('myLists.manage.customers.headers.push'),
        fields: ['push'],
      },
      // Composite: location with country and lang
      {
        id: 'location',
        header: t('myLists.manage.customers.headers.location'),
        fields: [{
          name: 'country',
          label: t('myLists.manage.customers.headers.country'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'countrySelect',
          },
        }, {
          name: 'lang',
          label: t('myLists.manage.customers.headers.lang'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'textInput',
          },
        }],
      },
      {
        id: 'lists',
        header: t('myLists.manage.customers.headers.lists'),
        fields: [],
      },
      // Hidden: expandable data for subscription status
      {
        id: 'additional_data',
        header: '',
        fields: [...useOptinFilterFields(), 'unsub_email', 'unsub_sms', 'unsub_push'],
        hidden: true,
      },
    ];

    // Facturation V5 : insère la colonne « Facturable » (badge + filtre) et
    // récupère `do_not_charge` (raison au survol). Masquée pour les offres V3/V4.
    if (isV5) {
      const extra = columns.find((c) => c.id === 'additional_data');
      if (extra && Array.isArray(extra.fields)) {
        extra.fields.push('do_not_charge');
      }
      const billableColumn: SpmColumnConfig = {
        id: 'billable',
        header: t('myLists.manage.customers.headers.billable'),
        fields: [{
          name: 'is_billable',
          label: t('myLists.manage.customers.headers.billable'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'singleSelect',
            options: [
              { label: t('myLists.manage.customers.billable.yes'), value: 1 },
              { label: t('myLists.manage.customers.billable.no'), value: 0 },
            ],
          },
        }],
      };
      const listsIndex = columns.findIndex((c) => c.id === 'lists');
      columns.splice(listsIndex >= 0 ? listsIndex : columns.length, 0, billableColumn);
    }

    onBeforeMount(async () => {
      listName.value = t('navbar.contacts');
    });

    // Helper to clean null/undefined strings
    const cleanStr = (str: string | null | undefined): string => {
      if (!str || str === 'null' || str === 'undefined') return '';
      return str;
    };

    // Helper to format full name
    const formatFullName = (firstName: string | null | undefined, lastName: string | null | undefined): string => {
      const first = cleanStr(firstName);
      const last = cleanStr(lastName);
      if (!first && !last) return '';
      return `${first} ${last}`.trim();
    };

    // Method to format phone numbers
    const formatPhoneNumber = (phone: string) => {
      if (!phone || phone === 'null') return '';
      // Remove all non-digit characters except +
      const cleaned = phone.replace(/[^\d+]/g, '');
      // Format with spaces every 2 digits for readability
      if (cleaned.startsWith('+')) {
        const countryCode = cleaned.slice(0, 3);
        const rest = cleaned.slice(3);
        return `${countryCode} ${rest.replace(/(\d{2})(?=\d)/g, '$1 ').trim()}`;
      }
      return cleaned.replace(/(\d{2})(?=\d)/g, '$1 ').trim();
    };

    /**
     * Load contact lists from API on hover
     */
    const loadContactLists = async (contactId: string | number) => {
      const id = String(contactId);

      // Initialize state if not exists
      if (!listsTooltipState[id]) {
        listsTooltipState[id] = {
          loading: false,
          error: false,
          lists: [],
          loaded: false,
        };
      }

      // Skip if already loaded or currently loading
      if (listsTooltipState[id].loaded || listsTooltipState[id].loading) {
        return;
      }

      // Set loading state
      listsTooltipState[id].loading = true;
      listsTooltipState[id].error = false;

      try {
        const response = await nestCall(
          'get',
          NestApiType.CLIENT_API,
          `/contacts/${id}/lists`,
          {}, // headers
          '', // query
          undefined, // data
          true, // secure
        );

        if (response && response.statusCode === 200 && response.data) {
          listsTooltipState[id].lists = response.data;
          listsTooltipState[id].loaded = true;
        } else {
          listsTooltipState[id].error = true;
        }
      } catch (error) {
        console.error('Error loading contact lists:', error);
        listsTooltipState[id].error = true;
      } finally {
        listsTooltipState[id].loading = false;
      }
    };

    /**
     * Get tooltip content based on current state
     */
    const getListsTooltipContent = (contactId: string | number): string => {
      const id = String(contactId);
      const state = listsTooltipState[id];

      if (!state) {
        return t('myLists.manage.customers.lists.loading');
      }

      if (state.loading) {
        return t('myLists.manage.customers.lists.loading');
      }

      if (state.error) {
        return t('myLists.manage.customers.lists.error');
      }

      if (state.loaded) {
        if (state.lists.length === 0) {
          return t('myLists.manage.customers.lists.noLists');
        }
        return state.lists.map((list) => `• ${list.name}`).join('\n');
      }

      return t('myLists.manage.customers.lists.loading');
    };

    /**
     * Get icon class based on lists state
     */
    const getListsIconClass = (contactId: string | number): string => {
      const id = String(contactId);
      const state = listsTooltipState[id];

      if (!state || state.loading || !state.loaded) {
        return 'cell-icon--inactive';
      }

      if (state.error) {
        return 'cell-icon--inactive';
      }

      return state.lists.length > 0 ? 'cell-icon--active' : 'cell-icon--inactive';
    };

    /**
     * Get unique key for tooltip to force re-render when state changes
     */
    const getListsTooltipKey = (contactId: string | number): string => {
      const id = String(contactId);
      const state = listsTooltipState[id];

      if (!state) return `lists-${id}-initial`;
      if (state.loading) return `lists-${id}-loading`;
      if (state.error) return `lists-${id}-error`;
      if (state.loaded) return `lists-${id}-loaded-${state.lists.length}`;

      return `lists-${id}-default`;
    };

    /**
     * Reset table selection after a grouped action is confirmed
     */
    const resetTableSelection = () => {
      if (childComponentRef.value?.unselectAll) {
        childComponentRef.value.unselectAll();
      }
    };

    /**
     * Reload table data after an action
     */
    const reloadTableData = () => {
      if (childComponentRef.value?.reloadData) {
        childComponentRef.value.reloadData();
      }
    };

    // ========== List Actions API ==========

    /**
     * Build selection payload for API
     * @param selectionInfo - Selection info from table (grouped mode)
     * @param rowId - Single row ID (row mode)
     * @returns Object with selection_type, selected_ids, and filters
     */
    const buildSelectionPayload = (
      selectionInfo?: SelectionInfo | null,
      rowId?: number | string | null,
    ): { selection_type: string; selected_ids: string[]; filters?: SpmApiFilter[] } => {
      if (selectionInfo) {
        // Grouped mode
        if (selectionInfo.selectAllMode) {
          return {
            selection_type: 'all',
            selected_ids: selectionInfo.excludedRows ?? [],
            filters: selectionInfo.filters,
          };
        }
        return {
          selection_type: 'manual',
          selected_ids: selectionInfo.selectedRows,
        };
      }
      // Single row mode
      return {
        selection_type: 'manual',
        selected_ids: rowId ? [String(rowId)] : [],
      };
    };

    /**
     * Call list actions API
     * @param payload - Action payload to send
     * @returns API response
     */
    const callListActionsApi = async (payload: Record<string, unknown>) => {
      try {
        const response = await nestCall(
          'post',
          NestApiType.CLIENT_API,
          '/list-actions',
          {}, // headers
          '', // query
          payload, // data (body)
          true, // secure
        );
        return response;
      } catch (error) {
        console.error('List action API error:', error);
        return { success: false, message: 'api_error' };
      }
    };

    /**
     * Handle API response and show appropriate toast
     * @param response - API response
     * @param successKey - Translation key for success message
     * @param isGrouped - Whether this was a grouped action
     */
    const handleActionResponse = (
      response: { success?: boolean; message?: string },
      successKey: string,
      isGrouped: boolean,
    ) => {
      if (response.success) {
        if (response.message === 'request_pending') {
          // Queued for async processing
          showToastSuccess(t('myLists.manage.customers.actions.queuedSuccess'));
        } else {
          // Immediate success
          showToastSuccess(t(successKey));
        }
        if (isGrouped) {
          resetTableSelection();
        }
        reloadTableData();
      } else {
        // Error
        const errorMessage = response.message || 'unknown_error';
        showToastError(t('myLists.manage.customers.actions.error', { message: errorMessage }));
      }
    };

    // ========== Action Confirm Handlers ==========

    const onCopyConfirm = async (result: CopyToListResult) => {
      setLoading(true);
      const selection = buildSelectionPayload(result.selectionInfo, result.rowId);
      const payload = {
        action_type: 'copy_to_list',
        ...selection,
        source_list_id: 'my_contacts',
        target_list_id: result.targetListId,
      };

      const response = await callListActionsApi(payload);
      handleActionResponse(
        response,
        'myLists.manage.customers.actions.copySuccess',
        result.context === 'grouped',
      );
      setLoading(false);
    };

    const onCreateConfirm = async (result: CreateListResult) => {
      setLoading(true);
      const selection = buildSelectionPayload(result.selectionInfo, result.rowId);
      const payload = {
        action_type: 'create_new_list',
        ...selection,
        source_list_id: 'my_contacts',
        new_list_name: result.listName,
      };

      const response = await callListActionsApi(payload);
      handleActionResponse(
        response,
        'myLists.manage.customers.actions.createListSuccess',
        result.context === 'grouped',
      );
      setLoading(false);
    };

    /**
     * Facturation V5 : applique set_billable / set_non_billable sur la sélection
     * (do_not_charge piloté côté back-end ; is_billable STORED recalculée).
     */
    const onBillableConfirm = async (result: SetBillableResult) => {
      setLoading(true);
      const selection = buildSelectionPayload(result.selectionInfo, result.rowId);
      const payload = {
        action_type: result.mode === 'billable' ? 'set_billable' : 'set_non_billable',
        ...selection,
        source_list_id: 'my_contacts',
      };

      const response = await callListActionsApi(payload);
      handleActionResponse(
        response,
        result.mode === 'billable'
          ? 'myLists.manage.customers.actions.setBillableSuccess'
          : 'myLists.manage.customers.actions.setNonBillableSuccess',
        result.context === 'grouped',
      );
      setLoading(false);
    };

    /**
     * RGPD (SC-3851) : efface définitivement les données personnelles des contacts
     * sélectionnés. Le mot de confirmation tapé par l'utilisateur est transmis à
     * l'API, qui le revérifie. Irréversible.
     */
    const onGdprEraseConfirm = async (result: GdprEraseResult) => {
      if (!result.rowId) return;
      setLoading(true);
      // Toujours un seul contact : la sélection n'est jamais reprise ici, et le
      // serveur refuse de son côté toute requête portant sur plus d'un contact.
      const payload = {
        action_type: 'gdpr_erase',
        selection_type: 'manual',
        selected_ids: [String(result.rowId)],
        source_list_id: 'my_contacts',
        confirmation: result.confirmation,
      };

      const response = await callListActionsApi(payload);
      handleActionResponse(
        response,
        'myLists.manage.customers.actions.gdprEraseSuccess',
        false,
      );
      setLoading(false);
    };

    /**
     * Libellé et classe CSS du statut d'abonnement d'une ligne sur un canal.
     * La décision est centralisée dans le composable, seule la présentation
     * reste locale.
     */
    const channelState = (
      channel: SubscriptionChannel,
      row: Record<string, any>,
    ): { label: string; cssClass: string } => {
      const presentation: Record<ChannelSubscriptionStatus, { label: string; cssClass: string }> = {
        no_coordinate: { label: t('myLists.manage.customers.expansion.status.notSubscribed'), cssClass: 'status-not-subscribed' },
        unsubscribed: { label: t('myLists.manage.customers.expansion.status.unsubscribed'), cssClass: 'status-unsubscribed' },
        subscribed: { label: t('myLists.manage.customers.expansion.status.subscribed'), cssClass: 'status-subscribed' },
        double_optin: { label: t('myLists.manage.customers.expansion.status.doubleOptin'), cssClass: 'status-double-optin' },
        simple_optin: { label: t('myLists.manage.customers.expansion.status.simpleOptin'), cssClass: 'status-simple-optin' },
        not_subscribed: { label: t('myLists.manage.customers.expansion.status.notSubscribed'), cssClass: 'status-not-subscribed' },
      };

      const coordinates: Record<SubscriptionChannel, unknown> = {
        email: row.email,
        sms: row.phone,
        push: row.push,
      };
      const optins: Record<SubscriptionChannel, number | string | null | undefined> = {
        email: row.optin_email,
        sms: row.optin_sms,
        push: null,
      };
      const unsubs: Record<SubscriptionChannel, number | string | null | undefined> = {
        email: row.unsub_email,
        sms: row.unsub_sms,
        push: row.unsub_push,
      };

      return presentation[getChannelSubscriptionStatus(
        channel,
        !!coordinates[channel],
        optins[channel],
        unsubs[channel],
      )];
    };

    /**
     * Handle export action from SpmTableAPI
     */
    const handleExport = async (payload: ExportPayload) => {
      try {
        setLoading(true);
        // Build export request payload - already simplified from SpmTableAPI
        const exportRequest: Record<string, unknown> = {
          entity_type: payload.exportConfig.entityType,
          selection_type: payload.selectAllMode ? 'all' : 'manual',
          selected_ids: payload.selectedIds,
          total_count: payload.totalCount,
          ...payload.exportConfig.additionalParams,
        };

        // Add filters only if present (selectAllMode from selection, not "Export all" button)
        if (payload.filters) {
          exportRequest.filters = payload.filters;
        }

        console.log('Export request:', exportRequest);

        const response = await nestCall(
          'post',
          NestApiType.CLIENT_API,
          '/export/init',
          {},
          '',
          exportRequest,
          true,
        );

        if (response && !response.error) {
          await showToastSuccess(t('myLists.manage.customers.export.success'));
        } else {
          await showToastError(t('myLists.manage.customers.export.error'));
        }
      } catch (error) {
        console.error('Export error:', error);
        await showToastError(t('myLists.manage.customers.export.error'));
      } finally {
        setLoading(false);
      }
    };

    return {
      columns,
      t,
      listName,
      childComponentRef,
      apiCall,
      formatPhoneNumber,
      formatFullName,
      cleanStr,
      loadContactLists,
      getListsTooltipContent,
      getListsTooltipKey,
      getListsIconClass,
      channelState,
      // Actions
      groupedActions,
      rowActions,
      copyToListRef,
      createListRef,
      setBillableRef,
      setNonBillableRef,
      gdprEraseRef,
      onCopyConfirm,
      onCreateConfirm,
      onBillableConfirm,
      onGdprEraseConfirm,
      handleExport,
    };
  },

});
</script>

<style scoped>
/* Expansion content styles - Minimalist design */
.expansion-content {
  display: flex;
  gap: 12px;
  padding: 12px 16px;
  background: #fafafa;
}

.channel-status {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border-radius: 6px;
  font-size: 0.8125rem;
  font-weight: 500;
  transition: all 0.15s ease;
}

.channel-status i {
  font-size: 0.875rem;
  opacity: 0.7;
}

.channel-status .channel-label {
  color: inherit;
  opacity: 0.8;
}

.channel-status .channel-value {
  margin-left: auto;
  font-weight: 600;
}

/* Status colors */
.channel-status.status-subscribed,
.channel-status.status-simple-optin {
  background: #ecfdf5;
  color: #047857;
}

.channel-status.status-double-optin {
  background: #eff6ff;
  color: #1d4ed8;
}

.channel-status.status-unsubscribed {
  background: #fef2f2;
  color: #dc2626;
}

.channel-status.status-not-subscribed {
  background: #f3f4f6;
  color: #6b7280;
}

/* Fullname composite column */
.fullname-cell {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.fullname-cell .name-row {
  display: flex;
  align-items: center;
}

.fullname-cell .contact-name {
  font-weight: 600;
  color: #1f2937;
}

.fullname-cell .contact-name--empty {
  font-weight: 400;
  font-style: italic;
  color: #9ca3af;
}

.fullname-cell .dates-row {
  display: flex;
  gap: 12px;
  font-size: 0.75rem;
  color: #6b7280;
}

.fullname-cell .date-info {
  display: flex;
  align-items: center;
  gap: 4px;
}

.fullname-cell .date-info i {
  font-size: 0.625rem;
  color: #9ca3af;
}

/* Location composite column */
.location-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.lang-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 6px;
  font-size: 0.625rem;
  font-weight: 600;
  color: #4b5563;
  background: #f3f4f6;
  border-radius: 3px;
  letter-spacing: 0.025em;
}

/* Cell icons */
.cell-icon {
  font-size: 1rem;
  display: block;
  text-align: center;
}

.cell-icon--active {
  color: #94c840;
}

.cell-icon--inactive {
  color: #d1d5db;
}

/* Empty cell */
.cell-empty {
  color: #9ca3af;
}

.cursor-pointer {
  cursor: pointer;
}

/* Facturation V5 : badge facturable / non-facturable */
.billable-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  font-size: 0.6875rem;
  font-weight: 600;
  border-radius: 9999px;
  text-transform: uppercase;
  letter-spacing: 0.025em;
  cursor: default;
}

.billable-badge--yes {
  color: #047857;
  background: #ecfdf5;
}

.billable-badge--no {
  color: #6b7280;
  background: #f3f4f6;
}
</style>

<style scoped lang="scss">
.newsletter-1 {
  color: $brand-orange;
}
.newsletter-2 {
  color: $brand-color-primary;
}

/* Page layout */
.list-contacts-page {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}

/* List type badge */
.list-type-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.625rem;
  background-color: var(--surface-100);
  color: var(--text-color-secondary);
  font-size: 0.75rem;
  font-weight: 500;
  border-radius: 4px;
  text-transform: uppercase;
  letter-spacing: 0.025em;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.header-alert {
  margin-top: 1rem;
}

/* Page Content */
.page-content {
  flex: 1;
}
</style>
